import { useEffect, useState } from 'react';
import {
  Button, FormField, InlineNote, Input, Modal, ModalActions, Select, Textarea,
} from '../../../components/shared';
import { listAllTeams, listDivisions, type DivisionDto, type TeamDto } from '../../../api/organization';
import { createBoard, type BoardDto, type BoardType } from '../../../api/boards';
import { tBoards } from '../../../lib/boards-i18n';

/**
 * 게시판 만들기 창 — 시안 v1 `list-create-dialog` (U2).
 *
 * 보내는 값은 현행 `BoardCreateDialog` 와 같다 — `name · description · boardType · scopeType · scopeId`.
 * 모양만 시안대로: 안내 한 줄 → 제목 · 설명 → 유형 카드 둘 → 분류 · 팀/부서 두 칸.
 *
 * ★ 만든 게시판은 **비공개로 시작한다**(서버 규칙) — 안내 문장이 그 사실을 말한다.
 * ★ 팀 · 부서 게시판은 대상을 골라야 저장된다 — 고르지 않은 채 저장하면 서버가 거절하기 전에 막는다.
 * ★ 창은 정본 `Modal`(포커스 가둠 · Esc) — 손으로 짠 배경을 두지 않는다.
 */

type Scope = 'PERSONAL' | 'TEAM' | 'DIVISION';

export interface BoardV1CreateDialogProps {
  onClose: () => void;
  onCreated: (board: BoardDto) => void;
}

type ApiError = { response?: { data?: { message?: string } }; message?: string };

export function BoardV1CreateDialog({ onClose, onCreated }: BoardV1CreateDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [boardType, setBoardType] = useState<BoardType>('CLASSIC');
  const [scope, setScope] = useState<Scope>('PERSONAL');
  const [scopeId, setScopeId] = useState('');
  const [divisions, setDivisions] = useState<DivisionDto[]>([]);
  const [teams, setTeams] = useState<Array<TeamDto & { divisionName: string | null }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listDivisions(), listAllTeams()])
      .then(([d, t]) => { if (!cancelled) { setDivisions(d); setTeams(t); } })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const needsTarget = scope !== 'PERSONAL';
  const canSave = name.trim().length > 0 && (!needsTarget || scopeId !== '') && !submitting;

  async function submit() {
    if (!canSave) return;
    setSubmitting(true);
    setError(null);
    try {
      const board = await createBoard({
        name: name.trim(),
        description: description.trim() || null,
        boardType,
        scopeType: scope,
        scopeId: needsTarget ? scopeId : null,
      });
      onCreated(board);
    } catch (e) {
      const err = e as ApiError;
      setError(tBoards('boards.create.err', { msg: err?.response?.data?.message ?? err?.message ?? 'unknown' }));
    } finally {
      setSubmitting(false);
    }
  }

  const targetOptions = scope === 'TEAM'
    ? teams.map((t) => ({ value: t.id, label: t.divisionName ? `${t.divisionName} · ${t.name}` : t.name }))
    : scope === 'DIVISION'
      ? divisions.map((d) => ({ value: d.id, label: d.name }))
      : [];

  return (
    <Modal
      title={tBoards('boards.create.heading')}
      size="sm"
      onClose={onClose}
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>{tBoards('boards.btn.cancel')}</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={!canSave} loading={submitting}>
            {tBoards('boards.btn.save')}
          </Button>
        </ModalActions>
      )}
    >
      <p className="bv1-create__lead">{tBoards('boards.v1.create.lead')}</p>

      <FormField label={`${tBoards('boards.settings.name')} *`}>
        <Input
          fullWidth
          autoFocus
          aria-required
          value={name}
          placeholder={tBoards('boards.v1.create.name_ph')}
          onChange={(e) => setName(e.target.value)}
        />
      </FormField>

      <FormField label={tBoards('boards.settings.description')}>
        <Textarea
          fullWidth
          value={description}
          placeholder={tBoards('boards.v1.create.desc_ph')}
          onChange={(e) => setDescription(e.target.value)}
        />
      </FormField>

      <fieldset className="bv1-create__types">
        <legend className="bv1-create__legend">{tBoards('boards.settings.type')}</legend>
        {(['CLASSIC', 'FEED'] as const).map((t) => (
          <label key={t} className={`bv1-typecard${boardType === t ? ' is-on' : ''}`}>
            <input
              type="radio"
              name="bv1-board-type"
              className="bv1-sr"
              checked={boardType === t}
              onChange={() => setBoardType(t)}
            />
            <b>{tBoards(t === 'CLASSIC' ? 'boards.settings.type_classic' : 'boards.settings.type_feed')}</b>
            <small>{tBoards(t === 'CLASSIC' ? 'boards.wide.type_classic_hint' : 'boards.wide.type_feed_hint')}</small>
          </label>
        ))}
      </fieldset>

      <div className="bv1-create__grid">
        <FormField label={tBoards('boards.create.scope')}>
          <Select
            portal
            value={scope}
            onChange={(v) => { setScope(v as Scope); setScopeId(''); }}
            options={[
              { value: 'PERSONAL', label: tBoards('boards.create.scope_personal') },
              { value: 'TEAM', label: tBoards('boards.create.scope_team') },
              { value: 'DIVISION', label: tBoards('boards.create.scope_division') },
            ]}
          />
        </FormField>
        <FormField label={tBoards('boards.v1.create.scope_target')}>
          <Select
            portal
            searchable
            value={scopeId || null}
            onChange={setScopeId}
            options={targetOptions}
            placeholder={tBoards('boards.v1.create.scope_none')}
            disabled={!needsTarget}
          />
        </FormField>
      </div>

      {error && <InlineNote tone="danger">{error}</InlineNote>}
    </Modal>
  );
}
